import {Rocket, TrendingUp} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import CreateSimLaunchForm from '@/components/CreateSimLaunchFormLazy'
import CreateSimPresaleForm from '@/components/CreateSimPresaleForm'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import { useSimLaunchpad } from '@/hooks/useSimLaunchpad'
import {
  PROTOCOL_CHAIN,
  PRESALE_LAUNCH_FEE_ETH,
  formatBps,
  PRESALE_FEE_BPS,
} from '@/lib/protocol'

type SubmissionType = 'launchpad' | 'presale'

function parseSubmissionType(value: string | null): SubmissionType {
  return value === 'presale' ? 'presale' : 'launchpad'
}

function SubmitProject() {
  const [searchParams, setSearchParams] = useSearchParams()
  const type = parseSubmissionType(searchParams.get('type'))
  const { walletWei } = useSimLaunchpad()

  const setType = (next: SubmissionType) => {
    setSearchParams({ type: next }, { replace: true })
  }

  return (
    <Layout>
      <SeoHead
        title="Launch a Project"
        description={`Anyone with a ${PROTOCOL_CHAIN} wallet can list a bonding-curve token or open a presale. No KYC.`}
      />

      <section>
        <div className="mx-auto max-w-4xl px-4 pb-2 pt-12 sm:px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Permissionless</p>
          <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">Launch a Project</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Anyone with a {PROTOCOL_CHAIN} wallet can list a bonding-curve token or open a presale. No KYC, no waitlist. Protocol presale fee is {PRESALE_LAUNCH_FEE_ETH} ETH; the create form sends the live factory launchFee().
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setType('launchpad')}
            aria-pressed={type === 'launchpad'}
            className={`flex items-center gap-3 rounded-3xl border p-4 text-left backdrop-blur-md transition-colors ${
              type === 'launchpad' ? 'border-lime-400/50 bg-lime-400/5' : 'border-white/10 bg-white/[0.03] hover:border-white/20'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
              <Rocket className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Launchpad</p>
              <p className="text-xs text-zinc-500">On-chain bonding curve</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setType('presale')}
            aria-pressed={type === 'presale'}
            className={`flex items-center gap-3 rounded-3xl border p-4 text-left backdrop-blur-md transition-colors ${
              type === 'presale' ? 'border-orange-400/50 bg-orange-400/5' : 'border-white/10 bg-white/[0.03] hover:border-white/20'
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-orange-400/20 bg-orange-400/10 text-orange-400">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Presale</p>
              <p className="text-xs text-zinc-500">
                Protocol {PRESALE_LAUNCH_FEE_ETH} ETH · name, ticker, supply, start/end · {formatBps(PRESALE_FEE_BPS)} of ETH raised
              </p>
            </div>
          </button>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
          {type === 'launchpad'
            ? <CreateSimLaunchForm walletWei={walletWei} />
            : <CreateSimPresaleForm />}
        </div>
      </section>
    </Layout>
  )
}

export default SubmitProject
